import type { Maybe } from '@xing-com/crate-common-graphql-types';
import { useLocation } from '@xing-com/crate-core-router';
import {
  AI_SEARCH_INIT_PROMPT_PARAM,
  AI_FABI_TRACKING_TOKEN_PARAM,
  SEARCH_STATE_PARAMS,
} from '@xing-com/crate-jobs-constants';
import type {
  JobItemResultFragment,
  JobSearchByQueryQuery,
  JobTeaserVisibleJobFragment,
} from '@xing-com/crate-jobs-graphql';

import { getNwtCleanQuery } from '../helpers/search-tracking-filters';

import { useJobsNwtTracking } from './use-jobs-nwt-tracking';
import { usePageTracking } from './use-page-tracking';

type TrackPageview = {
  results: JobSearchByQueryQuery['jobSearchByQuery'];
  sort?: string | null;
  page: number;
  filterCount: number;
  shouldTrackNwt?: boolean;
};

type JobItemResultVisibleJob = JobItemResultFragment & {
  jobDetail: JobTeaserVisibleJobFragment;
};

export const trackingFilterName: Record<string, string> = {
  remoteOption: 'remote',
  employmentType: 'type',
  careerLevel: 'level',
};

const isVisibleJob = (
  item: Maybe<JobItemResultFragment>
): item is JobItemResultVisibleJob => {
  return item?.jobDetail?.__typename === 'VisibleJob';
};

// Inferring
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useSearchTracking = () => {
  usePageTracking({ repeatTracking: true });

  const trackNwt = useJobsNwtTracking();
  const location = useLocation();

  const trackPageview = ({
    results,
    page,
    shouldTrackNwt,
  }: TrackPageview): void => {
    if (!results) {
      trackNwt({
        eventSchema: 'basic',
        event: 'viewed_screen',
        page: 'jobs_search/error',
      });

      return;
    }
    if (!shouldTrackNwt) {
      return;
    }

    const { searchQuery, total, trackingToken, boundedTotal } = results;

    const keywords = searchQuery.body?.keywords || undefined;
    const locationText =
      searchQuery.body?.location?.city?.name ||
      searchQuery.body?.location?.text ||
      undefined;
    const trackingTotal = boundedTotal ? boundedTotal.total : total;

    const query = new URLSearchParams(location.search);
    const originalTrackingToken = query.get(AI_FABI_TRACKING_TOKEN_PARAM);
    const hasInitPrompt = !!query.get(AI_SEARCH_INIT_PROMPT_PARAM);
    const hasFilters = !!searchQuery.body?.filterCollection?.length;
    const hasTimeFilters =
      !!query.get(SEARCH_STATE_PARAMS.sincePeriod) ||
      !!query.get(SEARCH_STATE_PARAMS.since);
    const isEmptyState =
      !keywords &&
      !locationText &&
      !hasFilters &&
      !hasTimeFilters &&
      !hasInitPrompt;

    trackNwt({
      event: 'search_performed',
      eventSchema: 'searchresult',
      element: 'job_posting',
      page: 'jobs_search/serp',
      query: getNwtCleanQuery(query),
      itemCount: trackingTotal,
      pageNumber: page,
      flags: `jobs_search_ai_search_true,jobs_search_empty_state_${isEmptyState}`,
      ...(trackingToken && { trackingToken }),
      ...(originalTrackingToken && { originalTrackingToken }),
    });
  };

  const trackFetchMore = ({
    results,
    page,
  }: Pick<TrackPageview, 'page' | 'results'>): void => {
    if (!results) {
      return;
    }

    const { collection, total, boundedTotal, searchQuery } = results;
    const visibleJobs = collection.filter(isVisibleJob);

    const token = visibleJobs[0]?.trackingToken;
    const tokenCommon = token?.split('.')[0];
    const query = new URLSearchParams(location.search);
    const trackingToken = query.get(AI_FABI_TRACKING_TOKEN_PARAM);
    const keywords = searchQuery.body?.keywords || undefined;
    const hasInitPrompt = !!query.get(AI_SEARCH_INIT_PROMPT_PARAM);
    const locationText =
      searchQuery.body?.location?.city?.name ||
      searchQuery.body?.location?.text ||
      undefined;
    const hasFilters = !!searchQuery.body?.filterCollection?.length;
    const hasTimeFilters =
      !!query.get(SEARCH_STATE_PARAMS.sincePeriod) ||
      !!query.get(SEARCH_STATE_PARAMS.since);
    const isEmptyState =
      !keywords &&
      !locationText &&
      !hasFilters &&
      !hasTimeFilters &&
      !hasInitPrompt;

    trackNwt({
      event: 'search_performed',
      eventSchema: 'searchresult',
      element: 'job_posting',
      page: 'jobs_search/serp',
      query: getNwtCleanQuery(query),
      itemCount: boundedTotal ? boundedTotal.total : total,
      pageNumber: page,
      trackingToken: tokenCommon,
      flags: `jobs_search_ai_search_true,jobs_search_empty_state_${isEmptyState}`,
      ...(trackingToken ? { originalTrackingToken: trackingToken } : {}),
    });
  };

  const trackFilterOpen = (filter = 'all_filters', context = 'serp'): void => {
    const filterName = trackingFilterName[filter] || filter;

    const contextString =
      context === 'serp'
        ? 'serp'
        : context === 'all_filters'
          ? 'serp_all_filters'
          : `serp_filter_${context}`;
    trackNwt({
      eventSchema: 'basic',
      event: 'clicked',
      page: 'jobs_search/serp',
      elementDetail: 'jobs_search_filter',
      element: `${filterName}_open`,
      context: contextString,
    });
  };

  const trackFilterClose = (
    filter: string,
    action: 'apply' | 'close' | 'cancel'
  ): void => {
    const filterName = trackingFilterName[filter] || filter;

    const element =
      filter === 'all_filters'
        ? `all_filters_${action}`
        : `filter_${filterName}_${action}`;
    const context =
      filter === 'all_filters'
        ? 'serp_all_filters'
        : `serp_filter_${filterName}`;
    trackNwt({
      eventSchema: 'basic',
      event: 'clicked',
      page: 'jobs_search/serp',
      elementDetail: 'jobs_search_filter',
      element,
      context,
    });
  };

  return {
    trackPageview,
    trackFetchMore,
    trackFilterOpen,
    trackFilterClose,
  };
};
